import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  LinearProgress,
  Stack,
  Typography,
} from "@mui/material";
import Select from "react-select";
import { axiosInstance } from "../../../utils/networking";
import { BulkReassignResult } from "../../../interfaces";
import {
  BULK_CHUNK_SIZE,
  BulkRunError,
  isInactiveUser,
  plural,
  runBulkReassign,
  userLabel,
} from "./caseManagementUtils";

const PRIMARY = "#9E1F63FF";
const buttonSx = { textTransform: "none", fontWeight: 600, borderRadius: 2 };

type Option = { value: string; label: string };

interface ReassignCasesDialogProps {
  open: boolean;
  caseIds: string[];
  /** GET /user/allUsers; null while loading, [] on failure. */
  users: any[] | null;
  onClose: () => void;
  onFinished: (result: BulkReassignResult) => void;
  onForbidden: () => void;
}

type Phase = "form" | "running" | "done";

// Successor picker mirrors OffboardUser: react-select over active users only.
const ReassignCasesDialog = ({
  open,
  caseIds,
  users,
  onClose,
  onFinished,
  onForbidden,
}: ReassignCasesDialogProps) => {
  const [phase, setPhase] = useState<Phase>("form");
  const [successor, setSuccessor] = useState<Option | null>(null);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [result, setResult] = useState<BulkReassignResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setPhase("form");
    setSuccessor(null);
    setProgress({ done: 0, total: 0 });
    setResult(null);
    setError(null);
  }, [open]);

  const options = useMemo<Option[]>(
    () =>
      (users || [])
        .filter((u) => u && u._id && !isInactiveUser(u))
        .map((u) => ({ value: String(u._id), label: userLabel(u) }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [users],
  );

  const count = caseIds.length;
  const busy = phase === "running";

  const submit = async () => {
    if (!successor || count === 0) return;
    const ids = [...caseIds];
    setPhase("running");
    setError(null);
    setProgress({ done: 0, total: Math.ceil(ids.length / BULK_CHUNK_SIZE) });
    try {
      const res = await runBulkReassign(
        ids,
        successor.value,
        (body) => axiosInstance.post(`/cases/bulk-reassign`, body).then((r) => r.data),
        (done, total) => setProgress({ done, total }),
      );
      setResult(res);
      setPhase("done");
      onFinished(res);
    } catch (err: any) {
      const cause = err instanceof BulkRunError ? (err.cause as any) : err;
      if (cause?.response?.status === 403) {
        onClose();
        onForbidden();
        return;
      }
      const message =
        cause?.response?.data?.message || cause?.message || "The server did not respond.";
      if (err instanceof BulkRunError && err.chunksDone > 0) {
        const partial = err.partial as BulkReassignResult;
        setResult(partial);
        setError(
          `Stopped after ${err.chunksDone} of ${Math.ceil(ids.length / BULK_CHUNK_SIZE)} chunks: ${message}`,
        );
        setPhase("done");
        onFinished(partial);
      } else {
        setError(`Nothing was reassigned: ${message}`);
        setPhase("form");
      }
    }
  };

  return (
    <Dialog
      open={open}
      onClose={() => !busy && onClose()}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3, p: 3, overflow: "visible" } }}
    >
      <Typography sx={{ fontWeight: 700, fontSize: 18, mb: 1 }}>
        {phase === "done" ? "Bulk reassign finished" : `Reassign ${plural(count, "case")}`}
      </Typography>

      {phase === "done" && result ? (
        <Stack spacing={1.5}>
          {error ? <Alert severity="warning">{error}</Alert> : null}
          <Alert severity={result.reassigned > 0 ? "success" : "info"}>
            Reassigned <b>{result.reassigned.toLocaleString()}</b>{" "}
            {result.reassigned === 1 ? "case" : "cases"} to {successor?.label}
            {result.skipped ? `, skipped ${result.skipped.toLocaleString()} (not found or deleted)` : ""}.
          </Alert>
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <Button
              variant="contained"
              disableElevation
              onClick={onClose}
              sx={{ ...buttonSx, backgroundColor: PRIMARY, "&:hover": { backgroundColor: "#7d184e" } }}
            >
              Done
            </Button>
          </Box>
        </Stack>
      ) : (
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            The selected cases get the chosen consultant as their primary BFC, each with a “BFC
            Reassigned” activity. Secondary BFCs are left as they are.
          </Typography>

          <Box>
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
              Reassign to
            </Typography>
            <Select
              value={successor}
              onChange={(v) => setSuccessor((v as Option) || null)}
              placeholder={users === null ? "Loading consultants…" : "Select an active consultant"}
              isLoading={users === null}
              isDisabled={busy}
              options={options}
              menuPortalTarget={typeof document !== "undefined" ? document.body : undefined}
              styles={{
                menuPortal: (base) => ({ ...base, zIndex: 2000 }),
                control: (provided) => ({ ...provided, minHeight: "44px" }),
                option: (provided, state) => ({
                  ...provided,
                  backgroundColor: state.isSelected ? "#9D2362" : state.isFocused ? "#F3F4F6" : "white",
                  color: state.isSelected ? "white" : "black",
                }),
              }}
            />
            {users !== null && options.length === 0 ? (
              <Typography variant="caption" color="error">
                Could not load consultants.
              </Typography>
            ) : null}
          </Box>

          {successor ? (
            <Alert severity="info" icon={false}>
              {plural(count, "case")} will be assigned to <b>{successor.label}</b>.
            </Alert>
          ) : null}

          {error ? <Alert severity="error">{error}</Alert> : null}

          {busy ? (
            <Box>
              <LinearProgress
                variant={progress.total > 1 ? "determinate" : "indeterminate"}
                value={progress.total ? (progress.done / progress.total) * 100 : 0}
                sx={{ "& .MuiLinearProgress-bar": { backgroundColor: PRIMARY } }}
              />
              <Typography variant="caption" color="text.secondary">
                Reassigning…{" "}
                {progress.total > 1
                  ? `chunk ${Math.min(progress.done + 1, progress.total)} of ${progress.total}`
                  : ""}
              </Typography>
            </Box>
          ) : null}

          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
            <Button
              variant="outlined"
              onClick={onClose}
              disabled={busy}
              sx={{ ...buttonSx, color: "text.primary", borderColor: "divider" }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              disableElevation
              onClick={submit}
              disabled={busy || !successor || count === 0}
              startIcon={busy ? <CircularProgress size={16} color="inherit" /> : undefined}
              sx={{ ...buttonSx, backgroundColor: PRIMARY, "&:hover": { backgroundColor: "#7d184e" } }}
            >
              {busy ? "Reassigning…" : `Reassign ${plural(count, "case")}`}
            </Button>
          </Box>
        </Stack>
      )}
    </Dialog>
  );
};

export default ReassignCasesDialog;
